import {defineStore} from 'pinia';
import {ref} from 'vue';
import {
  LEGACY_SEEN_VERSION,
  TOUR_CONFIG,
  TOUR_NAMES,
  cleanupDriverDOM,
  type TourName,
} from '@/ts/utils/tour-constants';
import {useUserStore} from '@/ts/store/user-store';

const DRIVERJS_INTERNAL_KEY = 'driver.js-done';

/**
 * Returns true if userVersion is outdated (less than currentVersion) or null
 */
const isVersionOutdated = (userVersion: number | null, currentVersion: number): boolean => {
  if (userVersion === null) {
    return true;
  }
  return userVersion < currentVersion;
};

/**
 * Reads the version stored in localStorage for a tour, or null if the browser has never seen it.
 *
 * Anything that is not a number came from the pre-versioning build, which wrote the string `"true"`
 * — see LEGACY_SEEN_VERSION for what that counts as.
 */
const readLocalVersion = (storageKey: string): number | null => {
  const raw = localStorage.getItem(storageKey);
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : LEGACY_SEEN_VERSION;
};

/**
 * Get the BE tour version field for a given tour.
 * Currently only onboardingTour has BE support via onboardingTourVersion.
 * Future tours will need their own BE fields or a generic tourVersions map.
 */
const getBEVersionForTour = (tourName: TourName, userData: ReturnType<typeof useUserStore>['userData']): number | null => {
  if (!userData) {
    return null;
  }

  // Map tour names to their BE version fields
  // Currently only onboardingTour is supported by BE
  if (tourName === TOUR_NAMES.onboardingTour) {
    return userData.onboardingTourVersion ?? null;
  }

  // Future tours without BE support return null (will use localStorage only)
  return null;
};

export const useTourStore =
  /** Manages product tour lifecycle, versioning, and trigger state */
  defineStore('tour-store', () => {
  const userStore = useUserStore();

  // Track the tour version localStorage last recorded, per tour (reactive map).
  // null = this browser has never seen the tour.
  const localStorageSeenMap = ref<Record<string, number | null>>({});

  // Flag to prevent callbacks from setting tour seen after logout
  const isCleared = ref<boolean>(false);

  // Trigger mechanism for starting tours from anywhere (e.g., sidebar icon)
  const pendingTourTrigger = ref<TourName | null>(null);

  // Initialize localStorage state for a tour
  const initTourState = (tourName: TourName): void => {
    const config = TOUR_CONFIG[tourName];
    if (!(tourName in localStorageSeenMap.value)) {
      localStorageSeenMap.value[tourName] = readLocalVersion(config.storageKey);
    }
  };

  /**
   * The newest version of a tour this user has finished, or null if they never have.
   *
   * Two sources, and the higher of them wins. The backend is the one that follows the user between
   * browsers, so it is what makes a version bump reach everybody; localStorage is what still answers
   * when the `PUT /me` behind a dismissal failed (useTour swallows that error by design) or before
   * `GET /me` has come back at all. Neither may veto the other: letting localStorage decide alone is
   * what kept the version bump from ever re-running this tour for a user who had dismissed it in
   * this browser, and letting the backend decide alone would re-run it on every login for anyone
   * whose write did not land.
   */
  const getSeenVersion = (tourName: TourName): number | null => {
    initTourState(tourName);

    const localVersion = localStorageSeenMap.value[tourName] ?? null;
    const beVersion = getBEVersionForTour(tourName, userStore.userData);

    if (localVersion === null) {
      return beVersion;
    }
    if (beVersion === null) {
      return localVersion;
    }
    return Math.max(localVersion, beVersion);
  };

  // Check if a tour should be shown
  const shouldShowTour = (tourName: TourName): boolean => {
    return isVersionOutdated(getSeenVersion(tourName), TOUR_CONFIG[tourName].version);
  };

  // Check if a tour has been seen
  const hasTourBeenSeen = (tourName: TourName): boolean => {
    return !shouldShowTour(tourName);
  };

  // Mark a tour as seen or not seen
  const setTourSeen = (tourName: TourName, value: boolean): void => {
    // Don't set if store has been cleared (logout)
    if (isCleared.value && value) {
      return;
    }

    const config = TOUR_CONFIG[tourName];

    // Update reactive state. Seen is recorded as the version that was seen, not as a bare boolean,
    // so the next version bump can tell what this browser has already been shown.
    localStorageSeenMap.value[tourName] = value ? config.version : null;

    // Update localStorage
    if (value) {
      localStorage.setItem(config.storageKey, String(config.version));
    } else {
      localStorage.removeItem(config.storageKey);
    }

    // Note: BE update is handled separately in use-product-tour.ts
  };

  const clearStore = (): void => {
    // Set flag to prevent callbacks from setting tour seen
    isCleared.value = true;

    // Cleanup any active tour DOM elements
    cleanupDriverDOM();

    // Clear all tour states
    for (const tourName of Object.keys(TOUR_CONFIG) as TourName[]) {
      const config = TOUR_CONFIG[tourName];
      localStorageSeenMap.value[tourName] = null;
      localStorage.removeItem(config.storageKey);
    }

    localStorage.removeItem(DRIVERJS_INTERNAL_KEY);
  };

  // Reset the cleared flag (called when initializing a new tour)
  const resetClearedFlag = (): void => {
    isCleared.value = false;
  };

  // Check if a tour is available for a given path (without language prefix)
  const isTourAvailableForPath = (tourName: TourName, pathWithoutLang: string): boolean => {
    const config = TOUR_CONFIG[tourName];
    return config.triggerPaths.some(triggerPath =>
      pathWithoutLang === triggerPath || pathWithoutLang === `${triggerPath}/`);
  };

  // Trigger a tour to start (used by sidebar icon)
  const triggerTour = (tourName: TourName): void => {
    pendingTourTrigger.value = tourName;
  };

  // Consume and clear the pending trigger (used by tour component)
  const consumeTourTrigger = (): TourName | null => {
    const trigger = pendingTourTrigger.value;
    pendingTourTrigger.value = null;
    return trigger;
  };

  return {
    // Generic methods for any tour
    shouldShowTour,
    hasTourBeenSeen,
    setTourSeen,
    getSeenVersion,

    // Tour trigger mechanism
    pendingTourTrigger,
    isTourAvailableForPath,
    triggerTour,
    consumeTourTrigger,

    // Utility methods
    clearStore,
    resetClearedFlag,
  };
});
